import { KubernetesObject } from "@kubernetes/client-node"
import ContainerFactory from "@yam-plugin/core-deploy/handlers/container"
import type { DeployWorkload } from "@yam-plugin/core-deploy/types.d"
import { trimAndLowerAndRemoveSlash } from "yam-operator/util"
import { OperateFunction, PlanContext, PlanContextData } from "yam-types"
import { Settings } from "../settings.d"
import { AccessApproachConfig } from "../types.d"
import { composeIngress } from "./ingress"
import { composeHeadlessSvc, composeLoadBalancer, composeService } from "./service"


const accessOperator: OperateFunction<AccessApproachConfig> = async (plan, diff, settings: Settings) => {
  if (diff.currentItems.length === 0) {
    plan.log.info(`'access' is not configured, operation skipped`)
    return
  }

  const createdServices = {} as { [key: string]: boolean }

  for (const access of diff.currentItems) {
    const serviceName = access.serviceName || plan.data.workloadName
    if (!createdServices[serviceName]) {
      // already created by previous item, this is not allowed
      createdServices[serviceName] = true
    } else {
      // serviceName must be unique, otherwise the port may conflicts
      plan.log.warn(`the access config will be ignored because serviceName is duplicated. host: ${JSON.stringify(access.host)}, serviceName: ${serviceName}`)
      continue
    }
    composeServiceAndIngress(access, plan, settings).forEach(r => {
      plan.action(async (ctx) => {
        ctx.mergeToYaml({
          name: r.metadata?.name + '-' + r.kind,
          content: r
        })
      }, `create-${r.kind?.toLowerCase()}:${r.metadata?.name}`)
    })
  }

  if (diff.deletedItems.length > 0) {
    for (const access of diff.deletedItems) {
      plan.log.warn(`deleted item detected, please delete it manually: ${JSON.stringify(access)}`)
    }
  }
}

const composeServiceAndIngress = (appAccess: AccessApproachConfig, plan: PlanContext, settings: Settings): KubernetesObject[] => {
  if (appAccess.serviceEnabled === false) {
    plan.log.info(`service '${appAccess.serviceName || appAccess.servicePort || appAccess.host}' is disabled, skip rendering.`)
    return []
  }
  const result: KubernetesObject[] = []
  const planData = plan.data
  const workload = planData.currentModelFull.deploy as DeployWorkload | undefined

  let defaultPort = 80
  if (workload?.containers && workload?.containers.length > 0) {
    if (workload?.containers[0].port) {
      defaultPort = parseInt(workload?.containers[0].port as string)
    } else if (workload?.containers[0].ports) {
      defaultPort = workload?.containers[0].ports[0].containerPort
    }
  }
  const containerPort = ContainerFactory.generatePortSpec(appAccess.containerPort || defaultPort)[0]

  // create Kubernetes ClusterIP/NodePort Service
  const genServiceNameFn = serviceName(appAccess.serviceName ?? planData)
  const servicePort = appAccess.servicePort || containerPort.containerPort
  const nodePort = appAccess.advancedFeatures?.nodePort
  result.push(composeService(plan, appAccess, genServiceNameFn, servicePort, containerPort, settings, nodePort))

  // create LoadBalancer service
  if (appAccess.loadBalancerEnabled) {
    result.push(composeLoadBalancer(plan, appAccess, genServiceNameFn, servicePort, containerPort, settings))
  }

  // create None-ClusterIP headless service
  if (appAccess.headlessSvcEnabled) {
    result.push(composeHeadlessSvc(plan, appAccess, genServiceNameFn, servicePort, containerPort, settings))
  }

  // create Ingress resource
  if (appAccess.ingressEnabled) {
    result.push(composeIngress(plan, appAccess, genServiceNameFn('').serviceName, containerPort, servicePort, settings))
  }
  return result
}


export type GenServiceName = (serviceSuffix: string) => { serviceName: string, serviceAliasName: string }

// serviceName returns a function used to generate serviceName and serviceAliasName
// serviceName('my-service')('-lb') 
//          ==> {serviceName: 'my-service-lb', aliasName: 'my-service-lb'}
//
// serviceName({app: 'my-app', envName: 'dev/xx/xx/xx'})('-lb') 
//          ==> {serviceName: 'my-app-lb-dev-xx-xx-xx', aliasName: 'my-service-lb'}
function serviceName(serviceNameParam: string | PlanContextData): GenServiceName {
  if (typeof serviceNameParam === "string") {
    return serviceSuffix => {
      const svcName = serviceNameParam + serviceSuffix
      return {
        serviceName: svcName,
        serviceAliasName: svcName
      }
    }
  } else {
    return serviceSuffix => {
      const envTmp = trimAndLowerAndRemoveSlash(serviceNameParam.envName)
      const svcName = serviceNameParam.app + serviceSuffix
      return {

        serviceName: svcName + (
          serviceNameParam.multiEnvMode
            // multi env mode, append env suffix to service name for environmental isolation
            ? ('-' + envTmp.replace(/\//g, '-'))
            // single env mode, no manipulation of service name, keep original service name for simpler service-to-service call
            : ''
        ),
        serviceAliasName: svcName
      }
    }
  }
}

export default accessOperator
